import {
  StockLogView,
  SuppliesDocumentKind,
  SuppliesEmirate,
  SuppliesFulfillmentStatus,
  SuppliesOrderPaymentStatus,
  SuppliesPaymentMethod,
  SuppliesPlacedOrderStatus,
} from "@/features/supplies/types";

/** Tailwind's `xl`: from here checkout lays out in two columns. */
export const SUPPLIES_WIDE_LAYOUT_QUERY = "(min-width: 80rem)";

/** The product's own name and mark, as a provider entry so it renders beside the other integrations. */
export const SUPPLIES_PROVIDER = {
  name: "ClinicSaver Supplies",
  logo: "/integrations/clinicsaver-supplies-mark.svg",
  icon: "solar:box-linear",
} as const;

export const SUPPLIES_PATHS = {
  inventory: "/supplies/inventory",
  item: (id: string) => `/supplies/inventory/${id}`,
  catalogue: "/supplies/catalogue",
  product: (id: string) => `/supplies/catalogue/${id}`,
  cart: "/supplies/cart",
  cartPanel: "/supplies/catalogue?cart=open",
  checkout: "/supplies/checkout",
  orders: "/supplies/orders",
  order: (id: string) => `/supplies/orders/${id}`,
  orderRequests: "/supplies/order-requests",
  orderRequest: (id: string) => `/supplies/order-requests/${id}`,
  settings: "/settings/supplies",
} as const;

export const CART_PANEL_PARAM = "cart";
export const CART_PANEL_OPEN = "open";

export const COLLECTION_AGENT_SENTENCE =
  "Each supplier invoices you directly. ClinicSaver FZ-LLC (ClinicPilot) collects the payment on their behalf.";

const NOT_CONNECTED_COPY =
  "This workspace isn't connected to Supplies. Connect it in Settings → Supplies to order.";
const RECONNECT_COPY =
  "The Supplies connection needs redoing. Disconnect and connect again in Settings → Supplies.";
const NO_ANSWER_COPY = "Supplies didn't answer in time. Try again in a minute.";
const NOT_AVAILABLE_COPY = "Supplies isn't available for this workspace.";
const CART_EMPTY_COPY = "The cart is empty.";
const ORDER_NOT_FOUND_COPY = "That order isn't in this workspace.";
const ORDER_DIDNT_CHECK_OUT_COPY =
  "Something in the order didn't check out. Review it and try again.";
const PAYMENT_NOT_STARTED_COPY =
  "The card payment couldn't be started. Try again, or pay from the order page.";

export const SUPPLIES_REQUEST_INVALID_COPY =
  "Something in the request didn't check out. Review it and try again.";

export const SUPPLIES_GENERAL_ERROR_COPY =
  "Something went wrong. Try again, and if it keeps happening contact your ClinicPilot admin.";

/** P12-9: a write whose outcome is unknown, after the re-read. */
export const SUPPLIES_UNKNOWN_OUTCOME_COPY =
  "We couldn't confirm that went through. We've reloaded the latest, check it before trying again.";

const LIST_CHANGED_COPY = "The list changed. Reload to start from the top.";

export const SUPPLIES_REFUSAL_COPY: Record<string, string> = {
  supplies_not_connected: NOT_CONNECTED_COPY,
  supplies_connection_broken: RECONNECT_COPY,
  supplies_unavailable: NO_ANSWER_COPY,
  supplies_timeout: NO_ANSWER_COPY,
  supplies_not_configured: NOT_AVAILABLE_COPY,
  supplies_disabled: NOT_AVAILABLE_COPY,
  supplies_clinics_only: NOT_AVAILABLE_COPY,
  supplies_clinicpilot_only: NOT_AVAILABLE_COPY,
  supplies_user_required: "Supplies needs a signed-in person.",
  supplies_already_connected:
    "This workspace is already connected. Reload to see it.",
  supplies_item_not_found: "That item isn't in this workspace's inventory.",
  supplies_item_update_empty: "Change at least one setting before saving.",
  supplies_variant_required: "Choose a size or option first.",
  supplies_variant_unknown: "That option isn't sold any more. Choose another.",
  supplies_stock_out_of_range: "That change would take the count out of range.",
  supplies_cart_line_not_found: "That cart line was already removed.",
  supplies_cart_empty: CART_EMPTY_COPY,
  basket_empty: CART_EMPTY_COPY,
  supplies_order_not_found: ORDER_NOT_FOUND_COPY,
  order_not_found: ORDER_NOT_FOUND_COPY,
  supplies_cart_order_pending: "Someone already ordered from this cart.",
  agreement_pending: "Accept the updated agreement to continue.",
  supplier_intake_paused:
    "A supplier in your cart isn't taking orders right now.",
  quote_changed: "Prices or availability changed since your quote.",
  quote_expired:
    "This quote has expired. Review the order again for current prices.",
  supplies_cart_changed:
    "Your cart changed after this quote. Review your order again.",
  quote_not_found: "That quote is no longer available. Review the order again.",
  address_not_found: "That delivery address was removed. Choose another.",
  saved_card_not_found: "That saved card isn't available. Choose another.",
  saved_card_required: "Choose a saved card.",
  saved_card_not_allowed: ORDER_DIDNT_CHECK_OUT_COPY,
  validation_failed: ORDER_DIDNT_CHECK_OUT_COPY,
  invalid_json: ORDER_DIDNT_CHECK_OUT_COPY,
  idempotency_key_reused: ORDER_DIDNT_CHECK_OUT_COPY,
  cod_unavailable: "Cash on delivery isn't available right now. Choose card.",
  basket_too_large: "The cart has too many lines. Remove some and try again.",
  buyer_unavailable:
    "Your Supplies account can't order right now. Contact support.",
  order_not_payable:
    "This order can't be paid now. It may already be paid or cancelled.",
  order_not_cancellable:
    "This order can't be cancelled any more. Items are already being prepared.",
  payment_already_captured:
    "The card was just charged. Cancel again in a moment to get a refund.",
  payment_in_progress:
    "A card payment is still going through. Try cancelling in a few minutes.",
  payment_not_refundable:
    "This payment can't be refunded automatically. Contact support.",
  payment_provider_error: PAYMENT_NOT_STARTED_COPY,
  payments_unavailable: PAYMENT_NOT_STARTED_COPY,
  documents_unavailable:
    "Documents aren't available right now. Try again later.",
  document_not_ready:
    "This document is still being prepared. Try again in a minute.",
  document_not_found: "That document isn't available.",
  not_draft: "This order request was already answered.",
  stale_lines:
    "Some items in this request can't be ordered any more. Ask the supplier to resend it.",
  acceptance_in_progress:
    "This order request is being accepted elsewhere. Reload in a moment.",
  request_not_found: "That order request isn't available any more.",
  agreement_version_superseded:
    "A newer version of this agreement was published. Review it again.",
  agreement_version_not_found:
    "That agreement version is no longer available. Review it again.",
  agreement_audience_mismatch:
    "This agreement isn't for your account. Contact support.",
  product_not_found: "That product is no longer available.",
  buyer_not_found: RECONNECT_COPY,
  invalid_cursor: LIST_CHANGED_COPY,
  supplies_cursor_invalid: LIST_CHANGED_COPY,
  supplies_log_cursor_invalid: LIST_CHANGED_COPY,
};

export const LINE_REFUSAL_REASON: Record<string, string> = {
  out_of_stock: "Out of stock",
  below_moq: "Below the minimum order",
  product_unavailable: "No longer available",
  supplier_unavailable: "Supplier unavailable",
  variant_required: "Choose an option",
  variant_stale: "This option changed",
  duplicate_line: "Listed twice",
  invalid_quantity: "Check the quantity",
  amount_out_of_range: "Quantity too large",
  selling_price_missing: "Not available to order",
  commission_rate_missing: "Not available to order",
  supplier_intake_paused: "Supplier not taking orders",
};
export const LINE_REFUSAL_FALLBACK_REASON = "Can't be ordered";

export const QUOTE_CHANGE_LINE_FIELD_LABEL: Record<string, string> = {
  unitPrice: "price per pack",
  listPrice: "list price",
  quantity: "quantity",
  freeQuantity: "free packs",
  subtotal: "subtotal",
  itemVat: "VAT",
  itemTotal: "line total",
  packageBrackets: "free-pack offer",
  supplierId: "supplier",
};
export const QUOTE_CHANGE_TOTAL_FIELD_LABEL: Record<string, string> = {
  productSubtotal: "Products",
  productVat: "Products VAT",
  productTotal: "Products total",
  deliveryFee: "Delivery fee",
  deliveryVat: "Delivery VAT",
  deliveryTotal: "Delivery total",
  codFee: "Cash on delivery fee",
  codFeeVat: "Cash on delivery fee VAT",
  codFeeTotal: "Cash on delivery total",
  total: "Total",
};
export const QUOTE_CHANGE_LINE_MONEY_FIELDS: ReadonlySet<string> = new Set([
  "unitPrice",
  "listPrice",
  "subtotal",
  "itemVat",
  "itemTotal",
]);

export const ORDER_STATUS_LABEL: Record<SuppliesPlacedOrderStatus, string> = {
  pending: "Awaiting payment",
  confirmed: "Confirmed",
  consolidating: "Being prepared",
  out_for_delivery: "Out for delivery",
  delivered: "Delivered",
  cancelled: "Cancelled",
};

export const FULFILLMENT_STATUS_LABEL: Record<
  SuppliesFulfillmentStatus,
  string
> = {
  pending: "Waiting",
  preparing: "Preparing",
  ready_for_pickup: "Ready for pickup",
  picked_up: "Picked up",
  delivered: "Delivered",
  cancelled: "Cancelled",
};

export const PAYMENT_METHOD_LABEL: Record<SuppliesPaymentMethod, string> = {
  card: "Card",
  saved_card: "Saved card",
  cod: "Cash on delivery",
};

export const PAYMENT_METHOD_DESCRIPTION: Record<SuppliesPaymentMethod, string> =
  {
    card: "Debit or credit card",
    saved_card: "A card kept from an earlier order",
    cod: "Pay in cash when the order arrives",
  };

export const PAYMENT_METHOD_ICON: Record<SuppliesPaymentMethod, string> = {
  card: "solar:card-linear",
  saved_card: "solar:card-2-linear",
  cod: "solar:wad-of-money-linear",
};

export const PAYMENT_STATUS_LABEL: Record<SuppliesOrderPaymentStatus, string> =
  {
    awaiting_payment: "Awaiting payment",
    pending_collection: "Pay on delivery",
    paid: "Paid",
    refunded: "Refunded",
    partially_refunded: "Partly refunded",
    cancelled: "Cancelled",
  };

export const DOCUMENT_KIND_LABEL: Record<SuppliesDocumentKind, string> = {
  sup: "Supplier invoice",
  del: "Delivery invoice",
  credit_note: "Credit note",
  proof_full: "Proof of delivery",
};

export const STOCK_SOURCE_LABEL: Record<StockLogView["source"], string> = {
  order_delivery: "Delivery",
  manual: "Adjustment",
  stocktake: "Stocktake",
};

export const SUGGESTION_REASON_LABEL = {
  below_reorder_level: "Below reorder level",
} as const;

export const NOT_FILLABLE_LABEL = {
  discontinued: "No longer sold",
  pack_size_check: "Check the pack size first",
} as const;

export const SUPPLIES_EMIRATES: readonly SuppliesEmirate[] = [
  "Abu Dhabi",
  "Dubai",
  "Sharjah",
  "Ajman",
  "Umm Al Quwain",
  "Ras Al Khaimah",
  "Fujairah",
];

/** All Supplies money is AED (P11-36); catalogue prices carry no currency, orders and quotes carry their own. */
export const SUPPLIES_CURRENCY = "AED";

export const CART_MAX_PACKS = 10_000;

/** The reorder fill DTO takes at most this many inventory ids. */
export const SUPPLIES_FILL_MAX_IDS = 200;

/** P12-20: after a payment is confirmed on this page, a pending order is polled for this long. */
export const SUPPLIES_PAYMENT_POLL_WINDOW_MS = 60_000;

/** A Supplies read younger than this is not re-read on mount; a write or a realtime event invalidates it sooner. */
export const SUPPLIES_READ_FRESH_MS = 30_000;

/** While an order on screen can still change in Supplies (webhook → mirror), it is re-read this often. */
export const SUPPLIES_OPEN_ORDER_POLL_MS = 30_000;

/** Statuses an order never leaves, so an order in one needs no polling. */
export const FINAL_ORDER_STATUSES: ReadonlySet<SuppliesPlacedOrderStatus> =
  new Set(["delivered", "cancelled"]);
